import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Minus } from "lucide-react";
import { PageMeta } from "@/components/common/PageMeta";
import { PageHero } from "@/components/common/PageHero";
import { PROBLEM_CATEGORIES, GOOD_FIT, NOT_FIT, ENGAGEMENT_ROUTES, HERO_ALT } from "@/lib/content";

export default function WhatWeSolve() {
  const [open, setOpen] = useState(0);
  return (
    <>
      <PageMeta title="What We Solve" description="DBRF works with organisations facing scientific or engineering questions too interconnected for a single specialist or vendor." />
      <PageHero
        eyebrow="Problem-led R&D"
        title="When the problem crosses disciplines, build the team around it."
        intro="DBRF works with organisations facing scientific or engineering questions that are important, poorly served by standard solutions and too interconnected for a single specialist or vendor."
        image="/assets/02-what-we-solve-hero.webp"
        alt={HERO_ALT["what-we-solve"]}
        objectPosition="center 35%"
        cta={[{ to: "/bring-a-challenge", label: "Describe your challenge", primary: true, testid: "wws-hero-cta" }]}
      />

      <section className="container-dbrf py-16 lg:py-24">
        <p className="eyebrow mb-4">Problem categories</p>
        <h2 className="max-w-2xl font-display font-bold text-navy" style={{ fontSize: "clamp(1.8rem,3vw,2.8rem)" }}>Where DBRF is most useful.</h2>
        <div className="mt-10 divide-y divide-[var(--line)] border-y border-[var(--line)]">
          {PROBLEM_CATEGORIES.map((c, i) => (
            <div key={c.title} data-testid={`problem-category-${i}`}>
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                aria-expanded={open === i}
                className="flex w-full items-center justify-between gap-4 py-5 text-left"
              >
                <span className="font-display text-lg font-semibold text-navy">{c.title}</span>
                <span className={`transition-transform ${open === i ? "rotate-45" : ""}`}><Minus size={0} /><span className="text-2xl text-[var(--cobalt)]">+</span></span>
              </button>
              {open === i && (
                <div className="grid gap-6 pb-8 md:grid-cols-2 lg:grid-cols-4">
                  {[["You may be facing…", c.facing], ["The problem often spans…", c.spans], ["A DBRF work package may include…", c.package], ["Possible outputs…", c.outputs]].map(([h, b]) => (
                    <div key={h} className="rounded-xl border border-[var(--line)] bg-[var(--paper)] p-4">
                      <p className="eyebrow mb-2 text-[0.68rem]">{h}</p>
                      <p className="text-sm text-[var(--mineral)]">{b}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-[var(--line)] bg-[var(--paper)] py-16 lg:py-24">
        <div className="container-dbrf grid gap-10 lg:grid-cols-2">
          <div>
            <p className="eyebrow mb-4 !text-[var(--success)]">Good fit</p>
            <ul className="space-y-3">
              {GOOD_FIT.map((x) => <li key={x} className="flex gap-3"><Check size={18} className="mt-0.5 flex-shrink-0 text-[var(--success)]" /><span className="text-navy">{x}</span></li>)}
            </ul>
          </div>
          <div>
            <p className="eyebrow mb-4 !text-[var(--error)]">Usually not a fit</p>
            <ul className="space-y-3">
              {NOT_FIT.map((x) => <li key={x} className="flex gap-3"><Minus size={18} className="mt-0.5 flex-shrink-0 text-[var(--error)]" /><span className="text-[var(--mineral)]">{x}</span></li>)}
            </ul>
          </div>
        </div>
      </section>

      <section className="container-dbrf py-16 lg:py-24">
        <p className="eyebrow mb-4">Engagement routes</p>
        <h2 className="font-display font-bold text-navy" style={{ fontSize: "clamp(1.6rem,2.6vw,2.4rem)" }}>Structured ways to begin.</h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ENGAGEMENT_ROUTES.map((r, i) => (
            <div key={r} className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-6">
              <span className="font-mono text-sm text-[var(--cobalt)]">{String(i + 1).padStart(2, "0")}</span>
              <p className="mt-2 font-display font-semibold text-navy">{r}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-[var(--mineral)]">Scope, governance and commercial structure are agreed for each engagement. DBRF does not promise universal performance transfer between audio, AI, health-adjacent or infrastructure workloads.</p>
        <Link to="/bring-a-challenge" data-testid="wws-final-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[var(--navy)] px-6 py-3.5 text-sm font-semibold text-white btn-transition hover:bg-[var(--cobalt)]">Describe your challenge <ArrowRight size={16} /></Link>
      </section>
    </>
  );
}
